import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { DocxExportReadSchema, type DocxExportRead } from '@/agent-v2/docx-export/schemas'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export type PostDocxExportRequest = {
  conversationId: string
  matterArtifactVersionId: string
  templateUserFileId: string
  /** The same value on a retry of one click, so the server starts the export once. */
  clientRequestId: string
}

/**
 * Start an Export as .docx. The server answers at once; the conversation receives a "started"
 * notice now and a "ready" or failure notice when the job ends.
 */
export async function postDocxExport(request: PostDocxExportRequest): Promise<DocxExportRead> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}/api/v2/matter-artifact/docx-exports`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
      body: JSON.stringify({
        conversation_id: request.conversationId,
        matter_artifact_version_id: request.matterArtifactVersionId,
        template_user_file_id: request.templateUserFileId,
        client_request_id: request.clientRequestId,
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = (parseError ? {} : data) as { detail?: unknown }
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Start Export as .docx failed - status: ${response.status}, detail: ${JSON.stringify(errorData.detail)}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            ...request,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, `Failed to start Export as .docx: ${response.status}`, errorData.detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Start Export as .docx response JSON parse failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), ...request, parseError: formatUnknownError(parseError) },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid Export as .docx response JSON')
    }

    const parsed = DocxExportReadSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Start Export as .docx response schema validation failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), ...request, responseKeys: getTopLevelKeys(data), issues: parsed.error.issues },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid Export as .docx response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
